import { Page } from "@mb-pro-ui/components";
import { FormControlLabel, Grid, Typography, TextField as TF, Checkbox, Autocomplete, Button } from "@mui/material";
import { Form, useField } from "react-final-form";
import { useIntl } from "react-intl";
import { useMutation } from "react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/Delete";

import { Company, RemoteFile } from "../utils/types";
import { useApi } from "../utils/ApiProvider";
import useEditedStyles, { useEditedColor } from "../hooks/useEditedStyles";
import TitledCard from "../components/templates/basic/TitledCard";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { ConfirmDialog, ConfirmDialogProps } from "../components/ConfirmDialog";
import { useSnackbar } from "notistack";

import type { paths } from "@helios/toolbox-types";
import { usePageSxProps } from "./styles";
import { TextField } from "../components/form/TextField";
import { APP_NAME } from "../App";
import { useUserInfo } from "../utils/user-info";
import { FormSkeleton } from "../components/FormSkeleton";
type CreateFileResponse = paths["/remote-files"]["post"]["responses"]["200"]["content"]["application/json"];

const CompanySelector = ({ companies, disabled }: { companies: Company[]; disabled?: boolean }) => {
  const [user] = useUserInfo();
  const {
    input,
    meta: { dirty = false, initial = null },
  } = useField<number | null>("company_ref", { allowNull: true });
  const [waitingForSelect, setWaitingForSelect] = useState(false);

  const { formatMessage } = useIntl();
  const { background } = useEditedColor();

  const checkOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        setWaitingForSelect(false);
        input.onChange(null);
      } else {
        setWaitingForSelect(true);
        input.onChange(initial);
      }
    },
    [input, initial]
  );

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Files" })} - ${APP_NAME}`;
  }, [formatMessage]);

  const renderCompanySelect = input.value !== null || waitingForSelect || !user?.rights?.global_remote_files;
  const selected = (companies.find(({ id }) => id === input.value) as Company) ?? null;

  return (
    <>
      {user?.rights.global_remote_files && (
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox onChange={checkOnChange} checked={input.value === null && !waitingForSelect} disabled={disabled} />}
            label={<Typography>{formatMessage({ defaultMessage: "Global" })}</Typography>}
            labelPlacement="end"
            sx={[{ m: 0, width: 1 / 1 }, (!renderCompanySelect || initial === null) && dirty && { backgroundColor: background }]}
          />
        </Grid>
      )}
      {renderCompanySelect ? (
        <Grid item xs={12}>
          <Autocomplete
            disablePortal
            options={companies}
            getOptionLabel={({ name }) => name}
            getOptionKey={({ id }) => `company-${id}`}
            fullWidth
            disableClearable={selected !== null}
            value={selected}
            onChange={(event, newValue) => {
              input.onChange(newValue?.id ?? null);
            }}
            renderInput={(params) => (
              <TF
                {...params}
                {...input}
                required={!user?.rights?.global_remote_files}
                InputLabelProps={{ shrink: true }}
                variant="filled"
                size="small"
                label={formatMessage({ defaultMessage: "Company" })}
                sx={[{ m: 0 }, dirty && { backgroundColor: background }]}
              />
            )}
            disabled={disabled}
          />
        </Grid>
      ) : null}
    </>
  );
};

const RemoteFilePage = () => {
  const [dialogProps, setDialogProps] = useState<Omit<ConfirmDialogProps, "open"> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { id } = useParams<{ id: string }>();
  const { singlePageSx } = usePageSxProps();
  const { formatMessage } = useIntl();
  const api = useApi();
  const editedStyle = useEditedStyles();
  const history = useHistory();
  const { closeSnackbar } = useSnackbar();
  const [user] = useUserInfo();

  const isNew = useMemo(() => id === "new", [id]);

  // TODO: további kezelt hibák felsorolása!!!
  const { errorHandler: onError } = useErrorHandler<RemoteFile>([
    [
      { status: 401, type: "remotefiles" },
      () => formatMessage({ defaultMessage: "You have no rights for remote file management." }),
      () => {
        history.push("/");
      },
    ],
    [{ status: 404 }, () => formatMessage({ defaultMessage: "Remote file not found." }), () => history.push("/files")],
    [
      { status: 400, matcher: ({ error, value }) => error?.message === `File already exists with this name ${value.filename}.` },
      () => formatMessage({ defaultMessage: "Remote file already exist with this name." }),
    ],
  ]);

  const emptyValue: RemoteFile = useMemo(() => ({ id: -1, content: "", filename: "", company_ref: null }), []);

  const fileQuery = () => (isNew ? Promise.resolve(emptyValue) : api<RemoteFile>(`/remote-files/${id}`));
  const companiesQuery = () => api<Company[]>("/companies");
  const saveQuery = useCallback(
    (file: RemoteFile) => {
      closeSnackbar();
      if (isNew) {
        const { id: _id, ...newFile } = file;
        return api(`/remote-files`, { method: "post", body: JSON.stringify(newFile) });
      }
      return api(`/remote-files/${id}`, { method: "post", body: JSON.stringify(file) });
    },
    [api, closeSnackbar, id, isNew]
  );
  const deleteQuery = () => api(`/remote-files/${id}`, { method: "delete" });

  const { mutate: getFileData, data: file, isLoading: fileIsLoading } = useMutation({ mutationFn: fileQuery, onError });
  const { mutate: getCompanies, data: companies, isLoading: companiesAreLoading } = useMutation({ mutationFn: companiesQuery, onError });

  const onSaveSuccess = useCallback(
    (response: any) => {
      if (isNew) {
        const { fileId } = response as CreateFileResponse;
        history.push(`/files/${fileId}`);
        return;
      }
      getFileData();
    },
    [getFileData, history, isNew]
  );
  const { mutate: saveFile, isLoading: saving } = useMutation({ mutationFn: saveQuery, onSuccess: onSaveSuccess, onError });
  const { mutate: deleteFile, isLoading: deleting } = useMutation({
    mutationFn: deleteQuery,
    onSuccess: () => {
      console.log("delete succes");
      history.push("/files");
    },
    onError,
  });

  const loading = useMemo(() => fileIsLoading || companiesAreLoading, [fileIsLoading, companiesAreLoading]);
  const saveAndDeleteDisabled = useMemo(() => saving || deleting, [saving, deleting]);

  const askRemoveFile = useCallback(() => {
    setDialogProps({
      title: formatMessage({ defaultMessage: "Delete remote file" }),
      message: formatMessage({ defaultMessage: "Are you sure to delete this file?" }),
      onClose: () => setDialogOpen(false),
      okAction: () => {
        setDialogOpen(false);
        deleteFile();
      },
      okLabel: formatMessage({ defaultMessage: "Delete" }),
      color: "error",
    });
    setDialogOpen(true);
  }, [deleteFile, formatMessage]);

  const onSubmit = useCallback(
    async (args: RemoteFile) => {
      saveFile(args);
    },
    [saveFile]
  );

  const unitidParse = useCallback((value: string) => {
    return value === "" ? null : parseInt(value);
  }, []);

  const unitidFormat = useCallback((value: unknown) => {
    return value === null || value === undefined || (typeof value === "number" && isNaN(value)) ? "" : `${value}`;
  }, []);

  useEffect(() => {
    const unregister = history.listen(() => {
      closeSnackbar();
    });

    return () => {
      unregister();
    };
  }, [history, closeSnackbar]);

  useEffect(() => {
    if (isNew) {
      return;
    }
    getFileData();
  }, [getFileData, isNew]);

  useEffect(() => {
    getCompanies();
  }, [getCompanies]);

  return (
    <Page sx={singlePageSx}>
      <Form<RemoteFile>
        onSubmit={onSubmit}
        initialValues={isNew ? emptyValue : file}
        render={({ handleSubmit, dirty }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item sx={{ flexGrow: 1 }}></Grid>
              {isNew ? null : (
                <Grid item>
                  <Button color="inherit" startIcon={<DeleteIcon />} sx={{ color: "error.main" }} onClick={askRemoveFile} disabled={saveAndDeleteDisabled}>
                    {formatMessage({ defaultMessage: "Remove" })}
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button
                  color="inherit"
                  startIcon={<SaveOutlinedIcon />}
                  className={dirty ? editedStyle.withHover : ""}
                  disabled={saveAndDeleteDisabled}
                  type="submit"
                >
                  {formatMessage({ defaultMessage: "Save" })}
                </Button>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <TitledCard title={formatMessage({ defaultMessage: "File data" })}>
                  <Grid container spacing={1} sx={{ p: 1 }}>
                    {loading ? (
                      <>
                        <Grid item xs={12}>
                          {user?.rights?.global_remote_files ? <FormSkeleton variant="checkbox" /> : <FormSkeleton variant="input" />}
                        </Grid>
                        <Grid item xs={12}>
                          <FormSkeleton variant="input" />
                        </Grid>
                        <Grid item xs={12}>
                          <FormSkeleton variant="input" />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <CompanySelector companies={companies ?? []} disabled={!isNew} />
                        <Grid item xs={12}>
                          <TextField
                            name="unitid"
                            disabled={!isNew}
                            label={formatMessage({ defaultMessage: "Helios ID" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0 }}
                            parse={unitidParse}
                            format={unitidFormat}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="filename"
                            required
                            disabled={!isNew}
                            label={formatMessage({ defaultMessage: "Filename" })}
                            variant="filled"
                            size="small"
                            fullWidth
                            sx={{ m: 0 }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </TitledCard>
              </Grid>
              <Grid item xs={12} sm={6} md={9}>
                <TitledCard title={formatMessage({ defaultMessage: "File content" })}>
                  <Grid container spacing={1} sx={{ p: 1 }}>
                    <Grid item xs={12}>
                      {loading ? (
                        <FormSkeleton variant="input" height={363} />
                      ) : (
                        <TextField
                          name="content"
                          multiline
                          minRows={15}
                          variant="filled"
                          size="small"
                          fullWidth
                          sx={{ m: 0 }}
                          inputProps={{ sx: { fontFamily: "monospace", maxHeight: 400 } }}
                          hiddenLabel
                        />
                      )}
                    </Grid>
                  </Grid>
                </TitledCard>
              </Grid>
            </Grid>
          </form>
        )}
      />
      {dialogProps ? <ConfirmDialog open={dialogOpen} {...dialogProps} /> : null}
    </Page>
  );
};

export default RemoteFilePage;
